/**
 * Os textos de venda do plano Pro. Desde a C5d (2026-10-21) vale a versão 'completa': o limite do Livre
 * (C5c) e a ordem da busca já existem.
 *
 * A versão 'neutra' fica como registro do que se dizia antes da C5d (só o que já valia: comece grátis
 * no Livre; o Pro é a assinatura mensal). Voltar a ela é trocar `VERSAO_DOS_TEXTOS`.
 *
 * A REGRA DA ORDEM: o Pro vem antes do Livre só na ordem padrão da busca ("Mais relevantes",
 * UsuarioController.ordenacao). Nas ordens que o cliente escolhe (avaliação, valor, nome) o plano não
 * pesa, e entre os Pros vale o critério de sempre. Por isso nenhum texto promete "o topo" ou "aparecer
 * primeiro" sem dizer que é na ordem padrão.
 *
 * O limite (C5c) já é verdade, e por isso fica fora da chave: as descrições do Livre dizem "com um
 * limite de novos clientes por mês" nas duas versões, e os textos do próprio limite (a folha do 409 na
 * Minha agenda, o aviso da última vaga e o contador) dizem "Com o Pro, não há limite de novos
 * clientes" sem passar por aqui (decisão de 2026-10-21).
 *
 * O aviso de "pagamento confirmado" do backend tem o mesmo par, em AssinaturaService.AVISO_PRO_ATIVO.
 * Os benefícios do Pro no catálogo (`planos.beneficios`) são editados pela tela Financeiro > Planos;
 * `beneficiosPro` abaixo é só o texto de reserva, para quando o catálogo não carregou.
 *
 * Preço: as funções que recebem `preco` usam o valor do catálogo. Só as frases do FAQ citam o valor
 * escrito (o FAQ é dado estático, ver a nota em pages/Faq/faqDef.js); as da Central de ajuda
 * (`ajuda*`) não citam valor nenhum, porque o teste dela recusa "R$".
 */
export const VERSAO_DOS_TEXTOS = 'completa';

/**
 * Fora da chave: verdade desde a C5c. É a frase dos textos do próprio limite -- a folha do 409 na
 * Minha agenda e o contador. O aviso da última vaga, no backend, diz o mesmo
 * (NotificacaoService.TEXTO_ULTIMA_VAGA_LIVRE).
 */
export const PRO_SEM_LIMITE = 'Com o Pro, não há limite de novos clientes.';

const TEXTOS = {
	/** Home, passo 3 do "Como funciona" para quem trabalha (o preço já está no cartão ao lado). */
	homePassoPlano: {
		neutra: 'Grátis. O Pro é opcional, sem fidelidade.',
		completa: 'Grátis. Com o Pro, novos clientes ilimitados e seu perfil na frente na busca padrão.',
	},
	/** FAQ "Quanto custa para trabalhar", primeira frase (antes da parte da comissão). */
	faqQuantoCusta: {
		neutra: 'Para começar, nada: o plano Livre é grátis, com um limite de novos clientes por mês. O Pro custa R$ 7,90 por mês.',
		completa: 'Para começar, nada: o plano Livre é grátis, com um limite de novos clientes por mês. O Pro custa R$ 7,90 por mês, libera novos clientes ilimitados e coloca seu perfil antes dos do Livre na ordem padrão da busca ("Mais relevantes").',
	},
	/** FAQ "Quanto custa para trabalhar", segunda resposta: o que cada plano faz. */
	faqDiferencaDosPlanos: {
		neutra: 'No plano Livre, seu perfil aparece nas buscas da sua cidade assim que o documento é aprovado, com um limite de novos clientes por mês. O Pro é a assinatura mensal, sem fidelidade.',
		completa: 'No plano Livre, seu perfil aparece nas buscas da sua cidade assim que o documento é aprovado, com um limite de novos clientes por mês. No Pro, os novos clientes são ilimitados e seu perfil aparece antes dos do Livre na ordem padrão da busca ("Mais relevantes"). Quando o cliente escolhe ordenar por avaliação, valor ou nome, vale a escolha dele.',
	},
	/** Central de ajuda, "Como ativar o perfil profissional", observação sobre quando o perfil aparece. */
	ajudaQuandoAparece: {
		neutra: 'No plano Livre, o perfil aparece nas buscas assim que o documento é aprovado, com um limite de novos clientes por mês. O Pro é uma assinatura mensal opcional.',
		completa: 'No plano Livre, o perfil aparece nas buscas assim que o documento é aprovado, com um limite de novos clientes por mês. O Pro libera novos clientes ilimitados e coloca seu perfil antes dos do Livre na ordem padrão da busca ("Mais relevantes").',
	},
	/** Central de ajuda, bloco "Assinatura do profissional" da página inicial da ajuda. */
	ajudaResumoAssinatura: {
		neutra: 'O profissional começa grátis no plano Livre, com um limite de novos clientes por mês. O Pro é uma mensalidade no Pix ou no cartão, sem fidelidade. Dá para pausar o perfil nas férias ou desativar quando quiser.',
		completa: 'O profissional começa grátis no plano Livre, com um limite de novos clientes por mês. O Pro, por mensalidade no Pix ou no cartão e sem fidelidade, libera novos clientes ilimitados e coloca o perfil antes dos do Livre na ordem padrão da busca. Dá para pausar o perfil nas férias ou desativar quando quiser.',
	},
	/** Funil /profissional/ativar, passo do plano: o que vem depois do clique. */
	ativarDepoisDoClique: {
		neutra: 'Depois você envia uma foto do seu documento. Aprovado o documento, seu perfil entra nas buscas no plano Livre, com um limite de novos clientes por mês. O Pro é opcional e pode ser assinado agora ou depois.',
		completa: 'Depois você envia uma foto do seu documento. Aprovado o documento, seu perfil entra nas buscas no plano Livre, com um limite de novos clientes por mês. O Pro libera novos clientes ilimitados e coloca você antes dos perfis do Livre na ordem padrão da busca.',
	},
	/** Meu cadastro > Assinatura, para quem está no Livre sem assinatura nenhuma. */
	assinaturaNoLivre: {
		neutra: 'Você está no plano Livre, com um limite de novos clientes por mês. O Pro é a assinatura mensal, sem fidelidade.',
		completa: 'Você está no plano Livre, com um limite de novos clientes por mês. Com o Pro, você recebe novos clientes sem limite e seu perfil aparece antes dos do Livre na ordem padrão da busca.',
	},
	/** Meu cadastro > Assinatura, selo do plano contratado quando o Pro está ativo. */
	assinaturaProAtivo: {
		neutra: 'Plano Pro ativo',
		completa: 'Pro ativo: novos clientes sem limite',
	},
};

/** Cadastro novo, etapa do plano: o subtítulo, com o preço do catálogo. */
const SUBTITULO_CADASTRO = {
	neutra: (preco) => `Sua conta já está criada. Comece grátis no plano Livre${preco ? `; o Pro custa ${preco}/mês` : ''}.`,
	completa: (preco) => `Sua conta já está criada. Comece grátis no plano Livre ou passe para o Pro${preco ? ` (${preco}/mês)` : ''}: novos clientes ilimitados e seu perfil na frente na busca padrão.`,
};

/** Benefícios de reserva do card do Pro, quando o catálogo não traz os seus. */
const BENEFICIOS_PRO = {
	neutra: ['Assinatura mensal, sem fidelidade', 'Pix ou cartão de crédito', 'Cancele quando quiser'],
	completa: ['Novos clientes ilimitados', 'Seu perfil antes dos do Livre na busca padrão', 'Cancele quando quiser'],
};

/** O texto de venda `chave` na versão vigente. */
export function textoDeVenda(chave) {
	const par = TEXTOS[chave];
	if (!par) throw new Error(`Texto de venda inexistente: ${chave}`);
	return par[VERSAO_DOS_TEXTOS];
}

export function subtituloDoCadastro(preco) {
	return SUBTITULO_CADASTRO[VERSAO_DOS_TEXTOS](preco);
}

export function beneficiosPro() {
	return BENEFICIOS_PRO[VERSAO_DOS_TEXTOS];
}

/** As duas versões de tudo, para o teste conferir que nenhuma promete o que a outra não deve. */
export const TODOS_OS_TEXTOS = { TEXTOS, SUBTITULO_CADASTRO, BENEFICIOS_PRO };
